import { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Heart, WarningCircle, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: request, 2: reset

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.requestPasswordReset(email);
      setSent(true);
      // In development, we get the token back
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
        setStep(2);
      }
      toast.success('Se ha enviado el enlace de recuperación');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(resetToken, newPassword);
      toast.success('Contraseña actualizada exitosamente');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al restablecer contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img src="/images/simuclinic-logo.png" alt="SimuClinic" className="h-12 mx-auto" style={{ objectFit: 'contain' }} />
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 && 'Recuperar Contraseña'}
              {step === 2 && 'Nueva Contraseña'}
              {step === 3 && 'Contraseña Actualizada'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Ingresa tu correo para recibir instrucciones'}
              {step === 2 && 'Ingresa tu nueva contraseña'}
              {step === 3 && 'Ya puedes iniciar sesión con tu nueva contraseña'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md flex items-center gap-2 text-sm mb-4">
                <WarningCircle size={18} />
                {error}
              </div>
            )}

            {step === 1 && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Correo electrónico</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Enviando...' : 'Enviar instrucciones'}
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="token">Token de recuperación</Label>
                  <Input
                    id="token"
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    required
                    data-testid="reset-token-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nueva contraseña</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    data-testid="reset-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="reset-confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Actualizando...' : 'Actualizar contraseña'}
                </Button>
              </form>
            )}

            {step === 3 && (
              <div className="text-center space-y-4">
                <CheckCircle size={48} className="mx-auto text-green-500" weight="fill" />
                <p className="text-slate-600">Tu contraseña ha sido actualizada correctamente.</p>
                <Button asChild className="w-full" data-testid="goto-login-btn">
                  <Link to="/login">Ir a Iniciar Sesión</Link>
                </Button>
              </div>
            )}

            <div className="text-center mt-4">
              <Link
                to="/login"
                className="text-sm text-blue-600 hover:underline"
                data-testid="back-to-login-link"
              >
                Volver a Iniciar Sesión
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
